import React, { useState } from 'react';
import { Lock, ShieldAlert, Send, Check, X, Clock } from 'lucide-react';
import { visiteAPI } from '../../services/api';
import '../../styles/AccessoRiservato.css';
import '../../styles/AccessoRiservatoConsole.css';

/**
 * AccessoRiservato — le due facce dell'autorizzazione a vedere una
 * procedura estetica riservata.
 *
 * PannelloRiservato   quello che vede chi NON e' autorizzato: cosa e
 *                     quando (che si vede sempre), e le due strade -
 *                     chiedere l'autorizzazione, o aprire in urgenza.
 * RichiesteInAttesa   quello che vede il medico che ha eseguito la
 *                     procedura: chi gli sta chiedendo di vederla, e i
 *                     due pulsanti per decidere.
 *
 * PERCHE' LA DECISIONE STA SULLA PROCEDURA E NON IN UNA SCHERMATA A PARTE
 * Per decidere serve sapere di cosa si parla. Un elenco di richieste
 * fuori contesto costringerebbe ad aprire il paziente per capire, e la
 * risposta piu' probabile diventerebbe "concedo, tanto non ricordo".
 * Qui il medico ha la sua procedura davanti mentre decide.
 *
 * PERCHE' L'URGENZA E' UN SECONDO PULSANTE E NON UN RIPIEGO NASCOSTO
 * Nasconderla la renderebbe inutile nel momento in cui serve - di notte,
 * a paziente presente, col collega non raggiungibile. Renderla comoda
 * come la strada normale la trasformerebbe nella strada normale. Sta in
 * chiaro, chiede il motivo per scritto, e avvisa l'esecutore.
 */

const ora = (d) => (d ? new Date(d).toLocaleString('it-IT', {
    dateStyle: 'short', timeStyle: 'short',
}) : '—');

const messaggio = (e, ripiego) =>
    e?.message || e?.response?.data?.message || ripiego;

/* ------------------------------------------------------------------ */
/* Chi non e' autorizzato                                             */
/* ------------------------------------------------------------------ */

export const PannelloRiservato = ({ visita, richiestaInAttesa, onRichiesta, onAperta }) => {
    const [modo, setModo] = useState(null);      // null | 'richiesta' | 'urgenza'
    const [motivo, setMotivo] = useState('');
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);

    /* Chi apre da se' - oggi il medico senior - non chiede niente a
       nessuno, ma l'apertura resta annotata e va detto prima, non dopo. */
    const apreDaSe = visita.riservata_apribile === true;

    const apri = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const r = await visiteAPI.apriRiservata(visita.id);
            if (onAperta) onAperta(r.data?.data?.visita);
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco ad aprire la procedura'));
        } finally {
            setInCorso(false);
        }
    };

    const chiedi = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const r = await visiteAPI.richiediAccesso(visita.id, motivo.trim() || undefined);
            setModo(null);
            setMotivo('');
            if (onRichiesta) onRichiesta(r.data?.data?.richiesta);
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco a inviare la richiesta'));
        } finally {
            setInCorso(false);
        }
    };

    const apriUrgenza = async () => {
        try {
            setInCorso(true);
            setErrore(null);
            const r = await visiteAPI.apriRiservata(visita.id, {
                urgenza: true, motivo: motivo.trim(),
            });
            if (onAperta) onAperta(r.data?.data?.visita);
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco ad aprire la procedura'));
        } finally {
            setInCorso(false);
        }
    };

    /* La soglia e' la stessa del backend. Duplicata di proposito: il
       controllo che conta e' quello del server, questo serve solo a non
       far premere un pulsante che dara' errore. */
    const motivoUrgenzaCorto = motivo.trim().length < 10;

    return (
        <div className="ar-riservata">
            <div className="ar-testata">
                <Lock size={20} strokeWidth={1.6} aria-hidden="true" />
                <div className="ar-sintesi">
                    <strong>Procedura riservata</strong>
                    <p>
                        {visita.sintesi_clinica || visita.patologia
                            || 'Trattamento registrato da un altro medico.'}
                    </p>
                    <small>
                        Cosa è stato fatto e quando lo vedi comunque. Il dettaglio —
                        fotografie, schema dei punti, prodotti, quantità e importi —
                        {apreDaSe
                            ? ' lo apri tu, e resterà scritto che l\'hai aperto: il medico'
                              + ' che ha eseguito la procedura può vederlo.'
                            : ' lo autorizza il medico che ha eseguito la procedura.'}
                    </small>
                </div>
            </div>

            {apreDaSe ? (
                <div className="ar-scelte">
                    <button className="ar-btn ar-btn--principale"
                            onClick={apri} disabled={inCorso}>
                        {inCorso ? 'Apro…' : 'Apri scheda completa'}
                    </button>
                    {errore && <div className="ar-errore">{errore}</div>}
                </div>
            ) : richiestaInAttesa ? (
                <div className="ar-attesa">
                    <Clock size={15} aria-hidden="true" />
                    <span>
                        Richiesta inviata il {ora(richiestaInAttesa.created_at)}: aspetta la
                        risposta del medico che ha eseguito la procedura.
                    </span>
                </div>
            ) : modo === null ? (
                <div className="ar-scelte">
                    <button className="ar-btn ar-btn--principale" onClick={() => setModo('richiesta')}>
                        <Send size={14} aria-hidden="true" /> Chiedi l'autorizzazione
                    </button>
                    <button className="ar-btn ar-btn--urgenza" onClick={() => setModo('urgenza')}>
                        <ShieldAlert size={14} aria-hidden="true" /> Apri per urgenza clinica
                    </button>
                </div>
            ) : (
                <div className="ar-modulo">
                    {modo === 'urgenza' && (
                        <p className="ar-avvertenza">
                            <ShieldAlert size={14} aria-hidden="true" />
                            Apre subito il dettaglio. Il medico che ha eseguito la procedura
                            riceve una notifica con il motivo che scrivi qui, e l'apertura
                            resta registrata come urgenza.
                        </p>
                    )}
                    <label htmlFor={`ar-motivo-${visita.id}`}>
                        {modo === 'urgenza'
                            ? 'Motivo clinico (obbligatorio)'
                            : 'Motivo della richiesta (facoltativo, aiuta chi deve decidere)'}
                    </label>
                    <textarea
                        id={`ar-motivo-${visita.id}`}
                        rows={2}
                        value={motivo}
                        onChange={(e) => setMotivo(e.target.value)}
                        placeholder={modo === 'urgenza'
                            ? 'Es. paziente in pronto soccorso, serve sapere il prodotto iniettato'
                            : 'Es. devo prescrivere un miorilassante'}
                    />
                    {errore && <div className="ar-errore">{errore}</div>}
                    <div className="ar-azioni">
                        <button className="ar-btn" onClick={() => { setModo(null); setErrore(null); }}>
                            Annulla
                        </button>
                        {modo === 'urgenza' ? (
                            <button
                                className="ar-btn ar-btn--urgenza"
                                onClick={apriUrgenza}
                                disabled={inCorso || motivoUrgenzaCorto}
                                title={motivoUrgenzaCorto
                                    ? 'Scrivi il motivo clinico, almeno dieci caratteri'
                                    : undefined}
                            >
                                {inCorso ? 'Apro…' : 'Apri e avvisa il collega'}
                            </button>
                        ) : (
                            <button
                                className="ar-btn ar-btn--principale"
                                onClick={chiedi}
                                disabled={inCorso}
                            >
                                {inCorso ? 'Invio…' : 'Invia la richiesta'}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/* ------------------------------------------------------------------ */
/* Il medico che ha eseguito la procedura                             */
/* ------------------------------------------------------------------ */

export const RichiesteInAttesa = ({ richieste, onDecisa }) => {
    const [inCorso, setInCorso] = useState(null);
    const [errore, setErrore] = useState(null);

    if (!richieste?.length) return null;

    const decidi = async (richiesta, concessa) => {
        try {
            setInCorso(richiesta.id);
            setErrore(null);
            await visiteAPI.decidiRichiesta(richiesta.id, concessa);
            if (onDecisa) onDecisa(richiesta.id, concessa);
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco a registrare la decisione'));
        } finally {
            setInCorso(null);
        }
    };

    return (
        <div className="ar-da-decidere">
            <strong>
                {richieste.length === 1
                    ? 'Un collega chiede di vedere questa procedura'
                    : `${richieste.length} colleghi chiedono di vedere questa procedura`}
            </strong>
            {errore && <div className="ar-errore">{errore}</div>}
            {richieste.map((r) => (
                <div key={r.id} className="ar-richiesta">
                    <div className="ar-richiesta-chi">
                        <b>
                            {[r.richiedente_cognome, r.richiedente_nome]
                                .filter(Boolean).join(' ') || 'Utente'}
                        </b>
                        <small>{ora(r.created_at)}</small>
                        {r.motivo && <p className="ar-richiesta-motivo">«{r.motivo}»</p>}
                    </div>
                    <div className="ar-richiesta-azioni">
                        <button
                            className="ar-btn ar-btn--principale"
                            onClick={() => decidi(r, true)}
                            disabled={inCorso === r.id}
                        >
                            <Check size={14} aria-hidden="true" /> Concedi
                        </button>
                        <button
                            className="ar-btn"
                            onClick={() => decidi(r, false)}
                            disabled={inCorso === r.id}
                        >
                            <X size={14} aria-hidden="true" /> Nega
                        </button>
                    </div>
                </div>
            ))}
            <small className="ar-nota">
                Concedendo, il collega vede il dettaglio per ventiquattro ore. Poi deve
                richiederlo.
            </small>
        </div>
    );
};
